import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { getBlobData, getData, patchDataToken } from "../axios/axiosHelper";
import {
  buildMerchantReviewGroups,
  buildReviewPayload,
  createReviewForm
} from "../features/userManagement/registrationReviewGroups";
import {
  buildRegistrationReviewApiQuery,
  buildRegistrationReviewQuery,
  normalizeRegistrationReviewFilters
} from "../features/userManagement/registrationReviewQuery";
import {
  clearRegistrationReviewQueueCache,
  getCachedRegistrationReviewQueue,
  loadRegistrationReviewQueue
} from "../features/userManagement/registrationReviewQueueCache";
import { protectedDocumentErrorMessage } from
  "../features/userManagement/protectedDocumentError";

const MESSAGES = {
  en: {
    loadError: "Unable to load registration reviews",
    reasonRequired: "A reason is required for rejection or re-upload",
    decisionRequired: "Choose a review decision",
    bilingualReasonRequired: "Enter the reason in both Arabic and English",
    merchantRejectAllRequired: "Reject the merchant registration once, not an individual document",
    updated: "Registration review updated"
  },
  ar: {
    loadError: "تعذر تحميل مراجعات التسجيل",
    reasonRequired: "سبب الرفض أو إعادة الرفع مطلوب",
    decisionRequired: "اختر قرار مراجعة واحداً على الأقل",
    bilingualReasonRequired: "أدخل السبب باللغتين العربية والإنجليزية",
    merchantRejectAllRequired: "ارفض تسجيل التاجر بالكامل مرة واحدة، وليس مستنداً منفرداً",
    updated: "تم تحديث مراجعة التسجيل"
  }
};

const canonicalReviewStatus = (item = {}) => {
  const explicit = item.reviewStatus || item.registrationReviewStatus;
  if (["APPROVED", "REJECTED", "REUPLOAD_REQUIRED"].includes(explicit)) {
    return explicit;
  }
  if (item.approvedByAdmin === true) return "APPROVED";
  const documentStatus =
    item.legalDocumentReviewStatus ||
    item.nationalIdDocumentStatus ||
    item.documentMetadata?.frontFile?.status ||
    item.documentMetadata?.commercialRegister?.status;
  return documentStatus || explicit || "PENDING_REVIEW";
};

const normalizeReviewItem = (item = {}) => {
  const profileImage = item.profileImage || item.image || item.logo;
  return {
    ...item,
    reviewStatus: canonicalReviewStatus(item),
    ...(profileImage ? { profileImage } : {})
  };
};

const formForDetail = (detail, type, language) => createReviewForm({
  language,
  groups: type === "merchants" ? buildMerchantReviewGroups(detail) : [],
  overallStatus: type === "merchants" ? detail?.reviewStatus : "",
  overallReason: type === "merchants" ? detail?.reviewReason : "",
  overallReasonTranslations:
    type === "merchants" ? detail?.reviewReasonTranslations : {}
});

const RegistrationReviewsHook = (language = "en") => {
  const messages = MESSAGES[language === "ar" ? "ar" : "en"];
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(
    () => normalizeRegistrationReviewFilters(searchParams),
    [searchParams]
  );
  const [searchDraft, setSearchDraft] = useState(filters.search);
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [documentLoading, setDocumentLoading] = useState("");
  const [documentViewer, setDocumentViewer] = useState(null);
  const [revealedNationalId, setRevealedNationalId] = useState("");
  const [nationalIdLoading, setNationalIdLoading] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: filters.page,
    totalPages: 1,
    totalResults: 0
  });
  const [reviewForm, setReviewForm] = useState(
    createReviewForm({ language })
  );
  const [showDecisionErrors, setShowDecisionErrors] = useState(false);
  const documentUrlRef = useRef(null);
  const documentRequestRef = useRef(0);
  const nationalIdRequestRef = useRef(0);
  const listRequestRef = useRef(0);

  const getErrorMessage = useCallback(
    (error) => error?.response?.data?.message || messages.loadError,
    [messages.loadError]
  );

  const setUrlFilters = useCallback(
    (patch, resetPage = true) => {
      const next = {
        ...filters,
        ...patch,
        page: Object.prototype.hasOwnProperty.call(patch, "page")
          ? patch.page
          : resetPage
            ? 1
            : filters.page
      };
      setSearchParams(buildRegistrationReviewQuery(next), { replace: true });
    },
    [filters, setSearchParams]
  );

  useEffect(() => {
    setSearchDraft(filters.search);
  }, [filters.search]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = searchDraft.trim();
      if (normalized === filters.search) return;
      setUrlFilters({ search: normalized });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [filters.search, searchDraft, setUrlFilters]);

  const loadItems = useCallback(({ force = false } = {}) => {
    const query = buildRegistrationReviewApiQuery(filters);
    query.delete("type");
    const url =
      `/admin/registration-reviews/${filters.type}?${query.toString()}`;
    const cached = force ? null : getCachedRegistrationReviewQueue(url);
    const requestId = listRequestRef.current + 1;
    listRequestRef.current = requestId;

    if (cached) {
      setItems((cached?.data?.data || []).map(normalizeReviewItem));
      setPagination(
        cached?.data?.pagination || {
          currentPage: filters.page,
          totalPages: 1,
          totalResults: 0
        }
      );
      setLoading(false);
    } else {
      setLoading(true);
    }

    return loadRegistrationReviewQueue(url, { force })
      .then((response) => {
        if (listRequestRef.current !== requestId) return;
        setItems((response?.data?.data || []).map(normalizeReviewItem));
        setPagination(
          response?.data?.pagination || {
            currentPage: filters.page,
            totalPages: 1,
            totalResults: 0
          }
        );
      })
      .catch((error) => {
        if (listRequestRef.current === requestId) {
          toast.error(getErrorMessage(error));
        }
      })
      .finally(() => {
        if (listRequestRef.current === requestId) setLoading(false);
      });
  }, [filters, getErrorMessage]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  useEffect(
    () => () => {
      listRequestRef.current += 1;
      if (documentUrlRef.current) {
        URL.revokeObjectURL(documentUrlRef.current);
        documentUrlRef.current = null;
      }
    },
    []
  );

  const closeDocumentViewer = useCallback(() => {
    documentRequestRef.current += 1;
    if (documentUrlRef.current) {
      URL.revokeObjectURL(documentUrlRef.current);
      documentUrlRef.current = null;
    }
    setDocumentViewer(null);
    setDocumentLoading("");
  }, []);

  const closeDetails = useCallback(() => {
    closeDocumentViewer();
    nationalIdRequestRef.current += 1;
    setRevealedNationalId("");
    setNationalIdLoading(false);
    setShowDetails(false);
    setSelectedItem(null);
  }, [closeDocumentViewer]);

  const changeType = (type) => {
    if (!["customers", "merchants"].includes(type)) return;
    closeDetails();
    listRequestRef.current += 1;
    setItems([]);
    setLoading(true);
    setReviewForm(createReviewForm({ language }));
    setShowDecisionErrors(false);
    setUrlFilters({ type });
  };

  const openDetails = (id) => {
    closeDocumentViewer();
    nationalIdRequestRef.current += 1;
    setRevealedNationalId("");
    setNationalIdLoading(false);
    setShowDetails(true);
    setDetailLoading(true);
    return getData(
      `/admin/registration-reviews/${filters.type}/${id}`,
      true
    )
      .then((response) => {
        const detailValue = response?.data?.data || null;
        const detail = detailValue ? normalizeReviewItem(detailValue) : null;
        setSelectedItem(detail);
        setReviewForm(formForDetail(detail, filters.type, language));
        setShowDecisionErrors(false);
      })
      .catch((error) => {
        toast.error(getErrorMessage(error));
        setShowDetails(false);
      })
      .finally(() => setDetailLoading(false));
  };

  const submitReview = () => {
    if (!selectedItem?.id) return Promise.resolve();
    let payload;
    try {
      payload = buildReviewPayload(reviewForm);
    } catch (error) {
      setShowDecisionErrors(true);
      const errorMessage = error.message === "REVIEW_DECISION_REQUIRED"
        ? messages.decisionRequired
        : error.message === "BILINGUAL_REVIEW_REASON_REQUIRED"
          ? messages.bilingualReasonRequired
        : error.message === "MERCHANT_REJECTION_MUST_APPLY_TO_ALL_GROUPS"
          ? messages.merchantRejectAllRequired
          : messages.reasonRequired;
      toast.error(
        errorMessage
      );
      return Promise.resolve();
    }
    setActionLoading("review");
    return patchDataToken(
      `/admin/registration-reviews/${filters.type}/${selectedItem.id}/review`,
      payload,
      true
    )
      .then((response) => {
        setShowDecisionErrors(false);
        toast.success(response?.data?.message || messages.updated);
        closeDetails();
        clearRegistrationReviewQueueCache();
        return loadItems({ force: true });
      })
      .catch((error) => toast.error(getErrorMessage(error)))
      .finally(() => setActionLoading(""));
  };

  const loadGalleryDocument = (items, index = 0) => {
    const item = items?.[index];
    if (!selectedItem?.id || !item) return Promise.resolve();
    const requestId = documentRequestRef.current + 1;
    documentRequestRef.current = requestId;
    setDocumentLoading(item.key || item.documentType);
    const endpoint = item.kind === "submission"
      ? `/admin/registration-reviews/${filters.type}/${selectedItem.id}/submissions/${item.submissionId}/groups/${item.group}/files/${item.fileId}`
      : `/admin/registration-reviews/${filters.type}/${selectedItem.id}/documents/${item.documentType}`;
    return getBlobData(
      endpoint,
      true,
      language
    )
      .then((response) => {
        if (documentRequestRef.current !== requestId) return;
        if (documentUrlRef.current) URL.revokeObjectURL(documentUrlRef.current);
        const url = URL.createObjectURL(response.data);
        documentUrlRef.current = url;
        setDocumentViewer({
          url,
          title: item.title,
          documentType: item.key || item.documentType,
          items,
          index
        });
      })
      .catch(async (error) =>
        toast.error(await protectedDocumentErrorMessage(error, language))
      )
      .finally(() => {
        if (documentRequestRef.current === requestId) setDocumentLoading("");
      });
  };

  const openDocument = (documentType, title = documentType) =>
    loadGalleryDocument(
      [{ key: documentType, kind: "legacy", documentType, title }],
      0
    );

  const openDocumentGallery = (items, index = 0) =>
    loadGalleryDocument(items, Math.max(0, index));

  const openSubmissionDocument = (submission, group, file, title) =>
    openDocumentGallery(
      [{
        key: `${submission?.id}:${group}:${file?.id}`,
        kind: "submission",
        submissionId: submission?.id,
        group,
        fileId: file?.id,
        title
      }],
      0
    );

  const navigateDocumentViewer = (delta) => {
    const items = documentViewer?.items || [];
    if (items.length < 2) return Promise.resolve();
    const nextIndex = (documentViewer.index + delta + items.length) % items.length;
    return loadGalleryDocument(items, nextIndex);
  };

  const toggleNationalId = () => {
    if (revealedNationalId) {
      nationalIdRequestRef.current += 1;
      setRevealedNationalId("");
      return Promise.resolve();
    }
    if (!selectedItem?.id || filters.type !== "customers") return Promise.resolve();
    const requestId = nationalIdRequestRef.current + 1;
    nationalIdRequestRef.current = requestId;
    setNationalIdLoading(true);
    return getData(
      `/admin/registration-reviews/customers/${selectedItem.id}/national-id`,
      true
    )
      .then((response) => {
        if (nationalIdRequestRef.current !== requestId) return;
        setRevealedNationalId(response?.data?.data?.nationalId || "");
      })
      .catch((error) => toast.error(getErrorMessage(error)))
      .finally(() => {
        if (nationalIdRequestRef.current === requestId) setNationalIdLoading(false);
      });
  };

  const updateReviewForm = (field, value) =>
    setReviewForm((previous) => ({ ...previous, [field]: value }));

  const updateReviewReason = (reasonLanguage, value) =>
    setReviewForm((previous) => ({
      ...previous,
      reasonTranslations: {
        ...previous.reasonTranslations,
        [reasonLanguage]: value
      }
    }));

  const selectReviewDecision = (decision) =>
    setReviewForm((previous) => ({
      ...previous,
      decision,
      reasonTranslations: decision === "APPROVED"
        ? { ar: "", en: "" }
        : previous.reasonTranslations
    }));

  const updateGroupReviewForm = (group, field, value) =>
    setReviewForm((previous) => ({
      ...previous,
      groupDecisions: {
        ...previous.groupDecisions,
        [group]: {
          ...previous.groupDecisions?.[group],
          [field]: value
        }
      }
    }));

  const updateGroupReviewReason = (group, reasonLanguage, value) =>
    setReviewForm((previous) => ({
      ...previous,
      groupDecisions: {
        ...previous.groupDecisions,
        [group]: {
          ...previous.groupDecisions?.[group],
          reasonTranslations: {
            ...previous.groupDecisions?.[group]?.reasonTranslations,
            [reasonLanguage]: value
          }
        }
      }
    }));

  const selectGroupReviewDecision = (group, decision) =>
    setReviewForm((previous) => ({
      ...previous,
      globalRejection: {
        ...previous.globalRejection,
        selected: false,
        reconsidering: Boolean(previous.globalRejection?.wasRejected)
      },
      groupDecisions: {
        ...previous.groupDecisions,
        [group]: {
          ...previous.groupDecisions?.[group],
          decision,
          reasonTranslations: decision === "APPROVED"
            ? { ar: "", en: "" }
            : previous.groupDecisions?.[group]?.reasonTranslations || {
                ar: "",
                en: ""
              }
        }
      }
    }));

  const setMerchantGlobalRejection = (selected) =>
    setReviewForm((previous) => ({
      ...previous,
      globalRejection: {
        ...previous.globalRejection,
        selected,
        reconsidering:
          Boolean(previous.globalRejection?.wasRejected) && !selected
      }
    }));

  const updateMerchantGlobalRejectionLanguage = (reasonLanguage) =>
    setReviewForm((previous) => ({
      ...previous,
      globalRejection: {
        ...previous.globalRejection,
        reasonLanguage
      }
    }));

  const updateMerchantGlobalRejectionReason = (reasonLanguage, value) =>
    setReviewForm((previous) => ({
      ...previous,
      globalRejection: {
        ...previous.globalRejection,
        reasonTranslations: {
          ...previous.globalRejection?.reasonTranslations,
          [reasonLanguage]: value
        }
      }
    }));

  const clearFilters = () => {
    setSearchDraft("");
    setSearchParams(
      buildRegistrationReviewQuery({ type: filters.type }),
      { replace: true }
    );
  };

  const refresh = useCallback(
    () => loadItems({ force: true }),
    [loadItems]
  );

  return {
    activeType: filters.type,
    filters,
    changeType,
    items,
    loading,
    initialLoading: loading && items.length === 0,
    refreshing: loading && items.length > 0,
    search: searchDraft,
    searchDraft,
    setSearchFilter: setSearchDraft,
    setSearchDraft,
    reviewStatus: filters.reviewStatus,
    setStatusFilter: (reviewStatus) => setUrlFilters({ reviewStatus }),
    updateFilters: setUrlFilters,
    clearFilters,
    currentPage: filters.page,
    pagination,
    setCurrentPage: (page) => setUrlFilters({ page }, false),
    openDetails,
    closeDetails,
    showDetails,
    selectedItem,
    detailLoading,
    actionLoading,
    documentLoading,
    documentViewer,
    revealedNationalId,
    nationalIdLoading,
    closeDocumentViewer,
    reviewForm,
    showDecisionErrors,
    updateReviewForm,
    updateReviewReason,
    selectReviewDecision,
    updateGroupReviewForm,
    updateGroupReviewReason,
    selectGroupReviewDecision,
    setMerchantGlobalRejection,
    updateMerchantGlobalRejectionLanguage,
    updateMerchantGlobalRejectionReason,
    submitReview,
    openDocument,
    openDocumentGallery,
    openSubmissionDocument,
    navigateDocumentViewer,
    toggleNationalId,
    refresh,
    hasActiveFilters: Boolean(
      filters.search ||
      filters.reviewStatus ||
      filters.datePreset !== "all" ||
      filters.sort === "registeredAt:asc"
    )
  };
};

export default RegistrationReviewsHook;
